/* eslint-disable prettier/prettier */
import { useContext, useEffect, useMemo, useState } from 'react';
import { addMonthsToDate, DateToLocalDateString } from 'components/util/Helpers/DateHelpers';
import { FiltersContext } from '../FiltersContext';
import moment from 'moment';

const formatTextDateType = (dateType: string) => {
  if (dateType === 'dtEmissao') {
    return 'Data de emissão';
  }
  return 'Data de agendamento';
};

export const useDateFilters = () => {
  const { filterTags, setFilterTags, filters, setFilters } = useContext(FiltersContext);

  const { finalIssueDate, initialIssueDate, finalScheduleDate, initialScheduleDate } = filters;

  const [dateType, setDateType] = useState('dtEmissao');

  const { initialDate, endDate } = useMemo(() => {
    const today = new Date();

    if (dateType === 'dtEmissao') {
      const endDate = finalIssueDate ? moment(finalIssueDate, 'DD/MM/YYYY').toDate() : today;
      const initialDate = initialIssueDate
        ? moment(initialIssueDate, 'DD/MM/YYYY').toDate()
        : addMonthsToDate(endDate, -1);

      return { initialDate, endDate };
    }

    const endDate = finalScheduleDate ? moment(finalScheduleDate, 'DD/MM/YYYY').toDate() : today;
    const initialDate = initialScheduleDate
      ? moment(initialScheduleDate, 'DD/MM/YYYY').toDate()
      : addMonthsToDate(endDate, -1);

    return { initialDate, endDate };
  }, [finalIssueDate, initialIssueDate, finalScheduleDate, initialScheduleDate, dateType]);

  const setInitialDate = (date: Date) => {
    if (dateType === 'dtEmissao') setFilters((prev) => ({ ...prev, initialIssueDate: DateToLocalDateString(date) }));
    else setFilters((prev) => ({ ...prev, initialScheduleDate: DateToLocalDateString(date) }));
  };

  const setEndDate = (date: Date) => {
    if (dateType === 'dtEmissao') setFilters((prev) => ({ ...prev, finalIssueDate: DateToLocalDateString(date) }));
    else setFilters((prev) => ({ ...prev, finalScheduleDate: DateToLocalDateString(date) }));
  };

  const handleChangeInitialDate = (date: Date) => {
    setInitialDate(date);
    setDateFilterTag(date, endDate);
  };

  const handleChangeEndDate = (date: Date) => {
    setEndDate(date);
    setDateFilterTag(initialDate, date);
  };

  const handleChangeDateType = (choice: string) => {
    setDateType(choice);
  };

  const setDateFilterTag = (initialDate: Date, endDate: Date) => {
    setFilterTags((prev) =>
      prev.map((tag) => {
        if (tag.filterName !== 'date') return tag;
        const value = `${DateToLocalDateString(initialDate)} - ${DateToLocalDateString(endDate)}`;
        const key = formatTextDateType(dateType);

        return { ...tag, key, value };
      })
    );
  };

  const areDateFiltersApplied = () => {
    const index = filterTags.findIndex((tag) => tag.filterName === 'date' && tag.value !== '');
    return index > -1;
  };

  const resetDateFilters = () => {
    setDateType('dtEmissao');
    const today = new Date();
    setEndDate(today);
    setInitialDate(addMonthsToDate(today, -1));
  };

  useEffect(() => {
    areDateFiltersApplied() && setDateFilterTag(initialDate, endDate);
  }, [dateType]);

  useEffect(() => {
    const initialDateValue = dateType === 'dtEmissao' ? initialIssueDate : initialScheduleDate;
    const endDateValue = dateType === 'dtEmissao' ? finalIssueDate : finalScheduleDate;

    setFilterTags((prev) => {
      const newFilterTags = prev.map((tag) => {
        const isInitialDate = !!initialDateValue;
        const isEndDate = !!endDateValue;
        if (!(isInitialDate || isEndDate) || tag.filterName !== 'date') return tag;

        return { ...tag, key: formatTextDateType(dateType), value: `${initialDateValue} - ${endDateValue}` };
      });
      return newFilterTags;
    });
  }, [
    setFilterTags,
    dateType,
    initialDate,
    endDate,
    finalIssueDate,
    initialIssueDate,
    finalScheduleDate,
    initialScheduleDate
  ]);

  return {
    initialDate,
    endDate,
    dateType,
    handleChangeInitialDate,
    handleChangeEndDate,
    handleChangeDateType,
    resetDateFilters
  };
};
